import type { QuestionAnalyticsItem } from "@/api/analytics"
import { DashboardResourceLink } from "@/components/Dashboard/DashboardResourceLink"
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu"
import { HtmlContent } from "../Common/HtmlContent"

interface QuestionAnalyticsTableProps {
  title?: string
  data: QuestionAnalyticsItem[]
}

type QuestionSortKey =
  | "questionNumber"
  | "questionText"
  | "correctnessRate"
  | "avgTimeSeconds"
  | "bookmarkCount"
  | "noteCount"
  | "subjects"
  | "topics"

type SortDirection = "asc" | "desc"

interface QuestionAnalyticsRow extends QuestionAnalyticsItem {
  questionNumber: number
  sourceIndex: number
}

const DEFAULT_SORT = {
  key: "questionNumber" as QuestionSortKey,
  direction: "asc" as SortDirection,
}

function getDefaultSortDirection(key: QuestionSortKey): SortDirection {
  if (key === "correctnessRate") {
    return "desc"
  }

  return "asc"
}

function getAriaSort(
  active: boolean,
  direction: SortDirection,
): "ascending" | "descending" | "none" {
  if (!active) {
    return "none"
  }

  return direction === "asc" ? "ascending" : "descending"
}

function compareQuestionAnalyticsRows(
  left: QuestionAnalyticsRow,
  right: QuestionAnalyticsRow,
  key: QuestionSortKey,
) {
  switch (key) {
    case "questionNumber":
      return left.questionNumber - right.questionNumber
    case "questionText":
      return left.question_text.localeCompare(right.question_text)
    case "correctnessRate":
      return left.correctness_rate - right.correctness_rate
    case "avgTimeSeconds":
      return left.avg_time_seconds - right.avg_time_seconds
    case "bookmarkCount":
      return (left.bookmark_count ?? 0) - (right.bookmark_count ?? 0)
    case "noteCount":
      return (left.note_count ?? 0) - (right.note_count ?? 0)
    case "subjects":
      return left.subject_names
        .join(", ")
        .localeCompare(right.subject_names.join(", "))
    case "topics":
      return left.topic_names
        .join(", ")
        .localeCompare(right.topic_names.join(", "))
  }
}

function SortIndicator({
  active,
  direction,
}: {
  active: boolean
  direction: SortDirection
}) {
  if (!active) {
    return <LuArrowUpDown size={14} aria-hidden="true" />
  }

  return direction === "asc" ? (
    <LuArrowUp size={14} aria-hidden="true" />
  ) : (
    <LuArrowDown size={14} aria-hidden="true" />
  )
}

function SortableHeaderButton({
  label,
  align = "left",
  active,
  direction,
  onClick,
}: {
  label: string
  align?: "left" | "right"
  active: boolean
  direction: SortDirection
  onClick: () => void
}) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      size="xs"
      w="full"
      minW="unset"
      px={0}
      color="inherit"
      aria-label={label}
      justifyContent={align === "right" ? "flex-end" : "flex-start"}
    >
      <Flex align="center" gap={1}>
        <Text as="span" fontWeight="semibold">
          {label}
        </Text>
        <SortIndicator active={active} direction={direction} />
      </Flex>
    </Button>
  )
}

export function QuestionAnalyticsTable({
  title = "Question Analytics",
  data,
}: QuestionAnalyticsTableProps) {
  const [sortState, setSortState] = useState(DEFAULT_SORT)
  const hasData = data.length > 0
  const rows = useMemo<QuestionAnalyticsRow[]>(
    () =>
      data.map((row, index) => ({
        ...row,
        questionNumber: index + 1,
        sourceIndex: index,
      })),
    [data],
  )

  const sortedRows = useMemo(() => {
    const directionMultiplier = sortState.direction === "asc" ? 1 : -1

    return [...rows].sort((left, right) => {
      const comparison = compareQuestionAnalyticsRows(
        left,
        right,
        sortState.key,
      )

      if (comparison !== 0) {
        return comparison * directionMultiplier
      }

      return left.sourceIndex - right.sourceIndex
    })
  }, [rows, sortState])

  const handleSort = (key: QuestionSortKey) => {
    setSortState((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        }
      }

      return {
        key,
        direction: getDefaultSortDirection(key),
      }
    })
  }

  const getSortState = (key: QuestionSortKey) => ({
    active: sortState.key === key,
    direction:
      sortState.key === key ? sortState.direction : DEFAULT_SORT.direction,
  })

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6} bg="bg.surface">
      <Heading size="lg" mb={4}>
        {title}
      </Heading>

      {!hasData ? (
        <Flex align="center" justify="center" minH="120px">
          <Text color="fg.muted">No question analytics available yet.</Text>
        </Flex>
      ) : (
        <>
          {/* Desktop */}
          <Box display={{ base: "none", md: "block" }}>
            <Table.Root variant="outline" size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader
                    w="84px"
                    aria-sort={getAriaSort(
                      getSortState("questionNumber").active,
                      getSortState("questionNumber").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Question #"
                      active={getSortState("questionNumber").active}
                      direction={getSortState("questionNumber").direction}
                      onClick={() => handleSort("questionNumber")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    aria-sort={getAriaSort(
                      getSortState("questionText").active,
                      getSortState("questionText").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Question"
                      active={getSortState("questionText").active}
                      direction={getSortState("questionText").direction}
                      onClick={() => handleSort("questionText")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    textAlign="right"
                    aria-sort={getAriaSort(
                      getSortState("correctnessRate").active,
                      getSortState("correctnessRate").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Correctness %"
                      align="right"
                      active={getSortState("correctnessRate").active}
                      direction={getSortState("correctnessRate").direction}
                      onClick={() => handleSort("correctnessRate")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    <SortableHeaderButton
                      label="Bookmarks"
                      align="right"
                      active={getSortState("bookmarkCount").active}
                      direction={getSortState("bookmarkCount").direction}
                      onClick={() => handleSort("bookmarkCount")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    <SortableHeaderButton
                      label="Notes"
                      align="right"
                      active={getSortState("noteCount").active}
                      direction={getSortState("noteCount").direction}
                      onClick={() => handleSort("noteCount")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    textAlign="right"
                    aria-sort={getAriaSort(
                      getSortState("avgTimeSeconds").active,
                      getSortState("avgTimeSeconds").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Avg Time (s)"
                      align="right"
                      active={getSortState("avgTimeSeconds").active}
                      direction={getSortState("avgTimeSeconds").direction}
                      onClick={() => handleSort("avgTimeSeconds")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    aria-sort={getAriaSort(
                      getSortState("subjects").active,
                      getSortState("subjects").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Subjects"
                      active={getSortState("subjects").active}
                      direction={getSortState("subjects").direction}
                      onClick={() => handleSort("subjects")}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    aria-sort={getAriaSort(
                      getSortState("topics").active,
                      getSortState("topics").direction,
                    )}
                  >
                    <SortableHeaderButton
                      label="Topics"
                      active={getSortState("topics").active}
                      direction={getSortState("topics").direction}
                      onClick={() => handleSort("topics")}
                    />
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedRows.map((row) => (
                  <Table.Row key={row.question_id} _hover={{ bg: "bg.subtle" }}>
                    <Table.Cell width="84px">{row.questionNumber}</Table.Cell>
                    <Table.Cell maxW="320px">
                      <Flex align="center" gap={2}>
                        <Box flex="1" minW={0}>
                          <HtmlContent
                            lineClamp={2}
                            content={row.question_text}
                          />
                        </Box>
                        <DashboardResourceLink
                          label="Open analytics"
                          to="/questions/$questionId/analytics"
                          params={{ questionId: row.question_id }}
                        />
                      </Flex>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {row.correctness_rate.toFixed(2)}%
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {row.bookmark_count ?? 0}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {row.note_count ?? 0}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {row.avg_time_seconds.toFixed(2)}
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap={2} wrap="wrap">
                        {row.subject_names.map((subject) => (
                          <Badge
                            key={subject}
                            colorPalette="blue"
                            variant="subtle"
                          >
                            {subject}
                          </Badge>
                        ))}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap={2} wrap="wrap">
                        {row.topic_names.map((topic) => (
                          <Badge
                            key={topic}
                            colorPalette="green"
                            variant="subtle"
                          >
                            {topic}
                          </Badge>
                        ))}
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile cards */}
          <VStack
            display={{ base: "flex", md: "none" }}
            gap={3}
            align="stretch"
          >
            {sortedRows.map((row) => (
              <Card.Root key={row.question_id}>
                <Card.Body>
                  <Flex align="flex-start" justify="space-between" gap={2}>
                    <Box flex="1" minW={0}>
                      <Text fontSize="xs" color="fg.muted" mb={1}>
                        Question {row.questionNumber}
                      </Text>
                      <HtmlContent lineClamp={2} content={row.question_text} />
                    </Box>
                    <DashboardResourceLink
                      label="Open analytics"
                      to="/questions/$questionId/analytics"
                      params={{ questionId: row.question_id }}
                    />
                  </Flex>
                  <SimpleGrid columns={2} gap={2} mt={3}>
                    <Stack gap={0}>
                      <Text fontSize="xs" color="fg.muted">
                        Correctness %
                      </Text>
                      <Text fontSize="sm">
                        {row.correctness_rate.toFixed(2)}%
                      </Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text fontSize="xs" color="fg.muted">
                        Avg Time (s)
                      </Text>
                      <Text fontSize="sm">
                        {row.avg_time_seconds.toFixed(2)}
                      </Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text fontSize="xs" color="fg.muted">
                        Bookmarks
                      </Text>
                      <Text fontSize="sm">{row.bookmark_count ?? 0}</Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text fontSize="xs" color="fg.muted">
                        Notes
                      </Text>
                      <Text fontSize="sm">{row.note_count ?? 0}</Text>
                    </Stack>
                  </SimpleGrid>
                  {row.subject_names.length > 0 && (
                    <Flex gap={1} wrap="wrap" mt={2}>
                      {row.subject_names.map((subject) => (
                        <Badge
                          key={subject}
                          colorPalette="blue"
                          variant="subtle"
                          size="sm"
                        >
                          {subject}
                        </Badge>
                      ))}
                    </Flex>
                  )}
                  {row.topic_names.length > 0 && (
                    <Flex gap={1} wrap="wrap" mt={1}>
                      {row.topic_names.map((topic) => (
                        <Badge
                          key={topic}
                          colorPalette="green"
                          variant="subtle"
                          size="sm"
                        >
                          {topic}
                        </Badge>
                      ))}
                    </Flex>
                  )}
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </>
      )}
    </Box>
  )
}
